'use client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '~/components/ui/dialog';
import { cn } from '~/lib/utils';
import { CTAButton } from '../common/cta-button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useFormState, useFormStatus } from 'react-dom';
import { SpinnerIcon } from '../assets/icons';
import { Button } from '../ui/Button';

interface InitialStateOptions {
  error?: Record<string, string | null>;
  success?: boolean;
}

const initialState: InitialStateOptions = {
  success: false,
  error: {
    firstName: null,
    lastName: null,
    email: null,
    companyName: null,
    jobTitle: null,
    message: null,
  },
} as const;

export function BusinessContactFormModal({
  submitRequest,
}: {
  submitRequest: (
    natureOfEnquiry: string,
    prevState: InitialStateOptions,
    formData: FormData,
  ) => Promise<InitialStateOptions>;
}) {
  const submitWithNatureOfEnquiry = submitRequest.bind(null, '[Pricing page] Requesting business access');
  const { pending } = useFormStatus();
  const [state, formAction] = useFormState(submitWithNatureOfEnquiry, initialState);

  if (state.success) {
    return (
      <Button disabled variant="secondary">
        Thanks for reaching out!
      </Button>
    );
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button>Request Access →</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Business account - request access</DialogTitle>
        </DialogHeader>
        {/* @ts-expect-error bad types */}
        <form action={formAction}>
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            <div>
              <Label htmlFor="first-name">First name</Label>
              <Label htmlFor="first-name" className="block text-red-400">
                {state.error?.firstName}
              </Label>
              <div className="mt-2.5">
                <Input type="text" name="first-name" id="first-name" autoComplete="given-name" />
              </div>
            </div>
            <div>
              <Label htmlFor="last-name">Last name</Label>
              <Label htmlFor="last-name" className="block text-red-400">
                {state.error?.lastName}
              </Label>
              <div className="mt-2.5">
                <Input type="text" name="last-name" id="last-name" autoComplete="family-name" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="email">Email</Label>
              {/* <p className="text-xs">This form does not accept gmail or other free domain email addresses.</p> */}
              <Label htmlFor="email" className="block text-red-400">
                {state.error?.email}
              </Label>
              <div className="mt-2.5">
                <Input type="email" name="email" id="email" autoComplete="email" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="company-name">Company name</Label>
              <Label htmlFor="company-name" className="block text-red-400">
                {state.error?.companyName}
              </Label>
              <div className="mt-2.5">
                <Input type="text" name="company-name" id="company-name" autoComplete="text" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="job-title">Job title</Label>
              <Label htmlFor="job-title" className="block text-red-400">
                {state.error?.jobTitle}
              </Label>
              <div className="mt-2.5">
                <Input type="text" name="job-title" id="job-title" autoComplete="text" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="message">Describe your use case and expected traffic</Label>
              <Label htmlFor="message" className="block text-red-400">
                {state.error?.message}
              </Label>
              <div className="mt-2.5">
                <Textarea name="message" id="message" rows={4} defaultValue={''} />
              </div>
            </div>
          </div>
          <div className="mt-8 flex justify-end">
            {/* <button
            type="submit"
            className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Send message
          </button> */}

            <CTAButton type="submit" className="inline-block" disabled={pending}>
              {pending ? <SpinnerIcon /> : 'Send message'}
            </CTAButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
